import { useEffect, useMemo, useState } from "react";
import api from "../api";
import "./HardwareAdmin.css";
import { useRef } from "react";

/* ================= CONSTANTS ================= */
const HARDWARE_TYPES = [
  "Keyboard",
  "Mouse",
  "Graphic Card",
  "CPU",
  "Screen",
  "Earphones",
  "RAM",
  "Processor",
  "Laptop",
];

const BRANDS = ["Dell", "Zebronics", "Logitech"];
const CITIES = ["Surat", "Ahmedabad", "Gandhinagar"];

const CARD_COLOR_CLASS = {
  Keyboard: "card-purple",
  Mouse: "card-blue",
  "Graphic Card": "card-orange",
  CPU: "card-red",
  Screen: "card-green",
  Earphones: "card-pink",
  RAM: "card-teal",
  Processor: "card-indigo",
  Laptop: "card-brown",
};

/* ================= EMPTY FORM ================= */
const emptyForm = {
  type: "",
  productBrand: "",
  pcName: "",
  location: "",
  serialNo: "",
  invoiceNo: "",
  vendorName: "",
  vendorContactNo: "",
  vendorEmail: "",
  ipAddress: "",
  purchaseDate: "",
  warrantyEndDate: "",
  purchaseValue: "",
  assignedUser: "",
};

export default function HardwareAdmin() {
  const [hardware, setHardware] = useState([]);
  const [users, setUsers] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editId, setEditId] = useState(null);
  const [activeType, setActiveType] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const detailRef = useRef(null);
  /* ================= LOAD DATA ================= */
  const loadData = async () => {
    const res = await api.get("/hardware");
    setHardware(res.data);
    const usersRes = await api.get("/users");
    setUsers(usersRes.data);
  };

  useEffect(() => {
    loadData();
  }, []);

  /* ================= CARD TOGGLE ================= */
  const handleCardClick = (type) => {
    setActiveType(type);

    // 👇 auto scroll to detail section
    setTimeout(() => {
      detailRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  /* ================= CARD COUNTS ================= */
  const hardwareCards = useMemo(() => {
    return HARDWARE_TYPES.map((type) => {
      const items = hardware.filter(
        (h) => h.type === type && h.status === "ACTIVE",
      );

      return {
        type,
        total: items.length,
        used: items.filter((h) => h.assignedUser).length,
        available: items.filter((h) => !h.assignedUser).length,
        surat: items.filter((h) => h.location === "Surat").length,
        ahmedabad: items.filter((h) => h.location === "Ahmedabad").length,
        gandhinagar: items.filter((h) => h.location === "Gandhinagar").length,
      };
    });
  }, [hardware]);

  /* ================= SAVE ================= */
  const handleSave = async (e) => {
    e.preventDefault();

    const payload = {
      ...form,
      purchaseDate: form.purchaseDate || null,
      warrantyEndDate: form.warrantyEndDate || null,
      purchaseValue: Number(form.purchaseValue) || 0,
      assignedUser: form.assignedUser || null,
    };

    if (isEdit) {
      await api.put(`/hardware/${editId}`, payload);
    } else {
      await api.post("/hardware", payload);
    }

    setShowAdd(false);
    setIsEdit(false);
    setEditId(null);
    setForm(emptyForm);
    setActiveType(null);
    loadData();
  };

  /* ================= EDIT ================= */
  const openEdit = (h) => {
    setForm({
      type: h.type || "",
      productBrand: h.productBrand || "",
      pcName: h.pcName || "",
      location: h.location || "",
      serialNo: h.serialNo || "",
      invoiceNo: h.invoiceNo || "",
      vendorName: h.vendorName || "",
      vendorContactNo: h.vendorContactNo || "",
      vendorEmail: h.vendorEmail || "",
      ipAddress: h.ipAddress || "",
      purchaseDate: h.purchaseDate?.slice(0, 10) || "",
      warrantyEndDate: h.warrantyEndDate?.slice(0, 10) || "",
      purchaseValue: h.purchaseValue || "",
      assignedUser: h.assignedUser?._id || "",
    });

    setEditId(h._id);
    setIsEdit(true);
    setShowAdd(true);
  };

  /* ================= DELETE ================= */
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this hardware?")) return;
    await api.delete(`/hardware/${id}`);
    setActiveType(null);
    loadData();
  };

  return (
    <div className="hardware-page">
      <div className="hardware-header">
        <h2>Hardware Master</h2>

        <div style={{ display: "flex", gap: 12 }}>
          <button className="primary-btn" onClick={() => setShowAdd(true)}>
            + Add Hardware
          </button>

          <button
            className="secondary-btn"
            onClick={() => {
              window.location.href = "/hardware-destroyed";
            }}
          >
            🗑 Destroyed Hardware
          </button>
        </div>
      </div>

      {/* ================= CARDS ================= */}
      <div className="hardware-card-grid">
        {hardwareCards.map((c) => (
          <div
            key={c.type}
            className={`hardware-card ${CARD_COLOR_CLASS[c.type]} ${
              activeType === c.type ? "active" : ""
            }`}
            onClick={() => handleCardClick(c.type)}
          >
            <div className="hw-card-header">
              <div>
                <h3>{c.type}</h3>
                <p>Hardware</p>
              </div>
            </div>

            <div className="card-stats colored">
              <div className="stat used">
                <span>Used</span>
                <strong>{c.used}</strong>
              </div>

              <div className="stat available">
                <span>Available</span>
                <strong>{c.available}</strong>
              </div>

              <div className="stat surat">
                <span>Surat</span>
                <strong>{c.surat}</strong>
              </div>

              <div className="stat ahmedabad">
                <span>Ahmedabad</span>
                <strong>{c.ahmedabad}</strong>
              </div>

              <div className="stat gandhinagar">
                <span>Gandhinagar</span>
                <strong>{c.gandhinagar}</strong>
              </div>
            </div>

            <div className="hw-total">
              <span>Total {c.type}</span>
              <strong>{c.total}</strong>
            </div>
          </div>
        ))}
      </div>

      {/* ================= DETAILS ================= */}
      {activeType && (
        <div ref={detailRef} className="hardware-detail-wrapper">
          <div className="hardware-detail-grid">
            {hardware
              .filter((h) => h.type === activeType && h.status === "ACTIVE")
              .map((h) => (
                <div key={h._id} className="hardware-detail-card">
                  <h4 className="detail-title">{h.type}</h4>

                  <p className="detail-subtitle">
                    {h.productBrand} – {h.pcName}
                  </p>

                  <DetailRow label="Type" value={h.type} />
                  <DetailRow label="Brand" value={h.productBrand} />
                  <DetailRow
                    label="Connection Type"
                    value={h.connectionType || "-"}
                  />
                  <DetailRow label="Serial No" value={h.serialNo} />
                  <DetailRow label="Location" value={h.location} />
                  <DetailRow label="Invoice No" value={h.invoiceNo || "-"} />
                  <DetailRow label="IP Address" value={h.ipAddress || "-"} />
                  <DetailRow label="Vendor Name" value={h.vendorName || "-"} />
                  <DetailRow
                    label="Vendor Contact"
                    value={h.vendorContactNo || "-"}
                  />
                  <DetailRow
                    label="Vendor Email"
                    value={h.vendorEmail || "-"}
                  />
                  <DetailRow
                    label="Purchase Value"
                    value={`₹ ${h.purchaseValue}`}
                  />

                  <div className="detail-row">
                    <span>Purchase Date</span>
                    <strong>
                      {h.purchaseDate
                        ? new Date(h.purchaseDate).toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })
                        : "-"}
                    </strong>
                  </div>

                  <DetailRow
                    label="Status"
                    value={
                      h.assignedUser
                        ? `Assigned to ${h.assignedUser.name}`
                        : "IN STOCK"
                    }
                  />

                  <div className="detail-actions">
                    <button onClick={() => openEdit(h)}>✏️ Edit</button>
                    <button
                      className="danger"
                      onClick={async () => {
                        const note = prompt("Reason for destroy?");
                        if (!note) return;

                        await api.post(`/hardware/${h._id}/destroy`, { note });
                        loadData();
                      }}
                    >
                      🗑 Destroy
                    </button>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* ================= ADD / EDIT DRAWER ================= */}
      {showAdd && (
        <div className="drawer-overlay">
          <div className="drawer">
            <div className="drawer-header">
              <h3>{isEdit ? "Edit Hardware" : "Add Hardware"}</h3>
              <button
                className="drawer-close"
                onClick={() => setShowAdd(false)}
              >
                ✖
              </button>
            </div>

            <form className="form-grid" onSubmit={handleSave}>
              <select
                required
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
              >
                <option value="">Select Hardware Type</option>
                {HARDWARE_TYPES.map((t) => (
                  <option key={t}>{t}</option>
                ))}
              </select>

              <select
                required
                value={form.productBrand}
                onChange={(e) =>
                  setForm({ ...form, productBrand: e.target.value })
                }
              >
                <option value="">Select Brand</option>
                {BRANDS.map((b) => (
                  <option key={b}>{b}</option>
                ))}
              </select>
              <select
                value={form.connectionType}
                onChange={(e) =>
                  setForm({ ...form, connectionType: e.target.value })
                }
              >
                <option value="">Connection Type</option>
                <option value="Wired">Wired</option>
                <option value="Wireless">Wireless</option>
                <option value="Both">Both</option>
              </select>
              <input
                required
                placeholder="PC / System Name"
                value={form.pcName}
                onChange={(e) => setForm({ ...form, pcName: e.target.value })}
              />
              <select
                required
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
              >
                <option value="">Select City</option>
                {CITIES.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>

              <input
                required
                placeholder="Serial Number"
                value={form.serialNo}
                onChange={(e) => setForm({ ...form, serialNo: e.target.value })}
              />
              <input
                placeholder="Invoice Number"
                value={form.invoiceNo}
                onChange={(e) =>
                  setForm({ ...form, invoiceNo: e.target.value })
                }
              />
              <input
                required
                placeholder="Vendor Name"
                value={form.vendorName}
                onChange={(e) =>
                  setForm({ ...form, vendorName: e.target.value })
                }
              />
              <input
                placeholder="Vendor Contact"
                value={form.vendorContactNo}
                onChange={(e) =>
                  setForm({ ...form, vendorContactNo: e.target.value })
                }
              />
              <input
                placeholder="Vendor Email"
                value={form.vendorEmail}
                onChange={(e) =>
                  setForm({ ...form, vendorEmail: e.target.value })
                }
              />
              <input
                placeholder="IP Address"
                value={form.ipAddress}
                onChange={(e) =>
                  setForm({ ...form, ipAddress: e.target.value })
                }
              />

              <select
                value={form.assignedUser}
                onChange={(e) =>
                  setForm({ ...form, assignedUser: e.target.value })
                }
              >
                <option value="">Keep in Stock</option>
                {users.map((u) => (
                  <option key={u._id} value={u._id}>
                    {u.name}
                  </option>
                ))}
              </select>

              <input
                type="date"
                value={form.purchaseDate}
                onChange={(e) =>
                  setForm({ ...form, purchaseDate: e.target.value })
                }
              />
              <input
                type="date"
                value={form.warrantyEndDate}
                onChange={(e) =>
                  setForm({ ...form, warrantyEndDate: e.target.value })
                }
              />
              <input
                type="number"
                placeholder="Purchase Value"
                value={form.purchaseValue}
                onChange={(e) =>
                  setForm({ ...form, purchaseValue: e.target.value })
                }
              />

              <div className="drawer-footer">
                <div className="drawer-actions">
                  <button className="primary-btn">
                    {isEdit ? "Update" : "Save"}
                  </button>
                  <button
                    type="button"
                    className="secondary-btn"
                    onClick={() => setShowAdd(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="detail-row">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}
